import React from 'react';
import { ErrorMessage, ErrorKey } from '../error-message';

/**
 * Properties for the Input component in a React application.
 */
export interface InputProps {
  /**
   * Label associated with the input.
   */
  label?: React.ReactNode;
  /**
   * HTML ID attribute for the input.
   */
  id?: string;
  /**
   * Indicates whether the input is required.
   */
  required?: boolean;
  /**
   * Additional CSS classes for styling the label.
   */
  labelClass?: string;
  /**
   * Additional CSS classes for styling the input wrapper.
   */
  wrapperClass?: string;
  /**
   * Additional CSS classes for styling the error wrapper.
   */
  errorClass?: string;
  /**
   * Color for warning text and border
   */
  warningColor?: string;
  /**
   * Additional CSS classes when the input is focused.
   */
  focusClass?: string;
  /**
   * Additional CSS classes for styling the focus outline.
   */
  focusOutlineClass?: string;
  /**
   *  Additional CSS classes for styling the input element.
   */
  className?: string;
  /**
   * Message associated with the input (custom error message or general message)
   */
  message?: string;
  /**
   * Event handler for the keydown event on the input.
   */
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  /**
   * Event handler for the Enter key press on the input.
   */
  onPressEnter?: React.KeyboardEventHandler<HTMLInputElement>;
  /**
   * State of the input: 'error', 'warning', or 'success'.
   */
  state?: 'error' | 'warning' | 'success';
  /**
   * A predefined error key from the ErrorKey enum.
   * Displays a predefined error message.
   */
  errorKey?: ErrorKey;
  /**
   * Indicates whether the input is in an error state.
   */
  hasError?: boolean;
  /**
   * Indicates whether the input is in a warning state.
   */
  hasWarning?: boolean;
  /**
   * Show error message when label is not provided.
   */
  alwaysShowInputWithMessage?: boolean;
  /**
   * Indicates whether the input is in read-only mode.
   */
  readOnly?: boolean;
  /**
   * Show icon in the input.
   */
  icon?: React.ReactNode;
}

export const Input = React.forwardRef<
  HTMLInputElement,
  React.PropsWithChildren<
    InputProps &
      React.DetailedHTMLProps<
        React.InputHTMLAttributes<HTMLInputElement>,
        HTMLInputElement
      >
  >
>(function InputWithRef(
  {
    label,
    id = Math.random().toString(),
    wrapperClass,
    errorClass,
    required,
    labelClass,
    message,
    state,
    className = '',
    focusClass = 'focus:!border-br-primary focus:!outline-none',
    focusOutlineClass = 'focus:ring-br-primary',
    onPressEnter,
    onKeyDown,
    errorKey,
    hasError,
    hasWarning,
    alwaysShowInputWithMessage,
    readOnly,
    icon,
    warningColor = 'yellow-500',
    ...otherProps
  },
  ref,
) {
  const input = (
    <input
      type={'text'}
      id={id}
      readOnly={readOnly}
      ref={ref}
      onKeyDown={(event) => {
        if (event.code === 'Enter') {
          onPressEnter?.(event);
        }
        onKeyDown?.(event);
      }}
      className={`block border ${
        otherProps.type === 'checkbox' || otherProps.type === 'radio'
          ? ''
          : 'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100'
      } placeholder-gray-400 dark:placeholder-gray-500 sm:text-sm ${
        icon ? 'pl-8' : ''
      } ${
        hasError
          ? 'border-error-base'
          : hasWarning
            ? `border-${warningColor}`
            : 'border-gray-300 dark:border-gray-600'
      } rounded-md shadow-sm disabled:bg-gray-100 dark:disabled:bg-gray-700 ${
        readOnly ? 'read-only:bg-gray-100 dark:read-only:bg-gray-700' : ''
      } ${
        hasError
          ? 'focus:ring-red-400'
          : hasWarning
            ? `focus:ring-${warningColor}`
            : focusOutlineClass
      } ${
        hasError
          ? 'focus:!border-error-base focus:!outline-none'
          : hasWarning
            ? `focus:!border-${warningColor} focus:!outline-none`
            : focusClass
      } ${className} ${
        otherProps.type === 'checkbox'
          ? 'text-br-primary h-4 w-4 !rounded dark:bg-gray-800 dark:checked:bg-gray-800'
          : ''
      } ${
        otherProps.type === 'radio'
          ? 'text-br-primary h-4 w-4 !rounded-full dark:bg-gray-800 dark:checked:bg-gray-800'
          : ''
      }`}
      {...otherProps}
    />
  );
  if (!label && !alwaysShowInputWithMessage) {
    return input;
  }
  return (
    <div className={wrapperClass}>
      {label && (
        <label
          htmlFor={id}
          className={`inline-flex text-sm font-medium text-gray-700 dark:text-gray-300 ${labelClass} mb-2`}
        >
          {label}
          {required && (
            <span className="ml-0.5 text-rose-600 dark:text-rose-400">*</span>
          )}
        </label>
      )}

      <div className="relative mt-1 flex items-center">
        {icon && (
          <div className="absolute left-3 text-gray-400 dark:text-gray-500">
            {icon}
          </div>
        )}
        {input}
      </div>

      {hasError ? (
        <ErrorMessage
          customClassName={errorClass}
          errorKey={errorKey}
          customErrorMessage={message}
        />
      ) : hasWarning ? (
        <div className={`flex text-sm text-${warningColor} ${errorClass}`}>
          {message}
        </div>
      ) : (
        message && (
          <p
            className={`text-sm text-gray-500 dark:text-gray-400 ${errorClass}`}
          >
            {message}
          </p>
        )
      )}
    </div>
  );
});
